import * as React from 'react';
import { styled } from '@glitz/react';
import noImage from './noimage';
import { findBrowserCachedResource, rememberBrowserCachedResource, showCacheImageUntilOriginalIsLoaded } from './cache';
import { type ImageUrlOptions, imageIsPreloaded, useImageUrls, useShouldEagerLoadImage } from './image-urls';
import { useImagePreset, useImageViewWidth } from './image-sizes';
import { imageWidth } from '.';

type PropType = React.ImgHTMLAttributes<HTMLImageElement> & {
  url: string;
  originalWidth?: number;
  originalHeight?: number;
  options?: ImageUrlOptions;
  preset?: Excite.ContentApi.ImagePreset;
};

export const Image = React.forwardRef<HTMLImageElement, PropType>(
  ({ url, originalHeight, originalWidth, options = {}, sizes, preset, ...restProps }, ref) => {
    const fallbackRef = React.useRef<HTMLImageElement>(null);
    const imageRef = (ref as React.RefObject<HTMLImageElement | null>) ?? fallbackRef;
    const isPreloaded = imageIsPreloaded();
    const internalPreset = useImagePreset();
    const imageViewWidth = useImageViewWidth();
    const internalSizes = sizes ?? imageViewWidth;
    options.preset = preset ?? internalPreset;

    const { src, srcSet } = useImageUrls(url, imageWidth[options.preset], imageRef, options);
    const eager = useShouldEagerLoadImage(url, options?.forceEagerLoading);
    const cachedSrc = findBrowserCachedResource(url)?.src;
    const useCachedSrc = Boolean(cachedSrc);
    showCacheImageUntilOriginalIsLoaded(src, cachedSrc, imageRef, srcSet);

    return (
      <ImageBase
        // We set the images opacity to 1 in excite/server/html-skeleton.tsx, to get the images to fade in.
        // React 18 doesn't like this since it causes a mismatch between what is rendered on the server and on the client.
        // We know what we are doing here, hence we suppress the warning.
        suppressHydrationWarning
        loading={eager ? 'eager' : 'lazy'}
        ref={imageRef}
        key={url}
        css={(useCachedSrc || isPreloaded) && cachedImageDecorator}
        {...restProps}
        height={originalHeight}
        width={originalWidth}
        srcSet={useCachedSrc ? undefined : srcSet}
        src={useCachedSrc ? cachedSrc : src}
        sizes={internalSizes}
        onLoad={e => {
          e.currentTarget.style.opacity = '1';
          rememberBrowserCachedResource(e.currentTarget, url);
          restProps.onLoad?.(e);
        }}
        onError={e => {
          e.currentTarget.srcset = '';
          e.currentTarget.src = noImage as unknown as string;
          restProps.onError?.(e);
        }}
      />
    );
  },
);

const cachedImageDecorator = styled({
  opacity: 1,
});

const ImageBase = styled.img({
  maxWidth: '100%',
  height: 'auto',
  opacity: 0,
  transition: {
    property: 'opacity',
    duration: '200ms',
    timingFunction: 'ease-in',
  },
});

export const PlaceholderImage = styled(ImageBase, { backgroundColor: 'rgba(0, 0, 0, .05)' });
